import type { UploadFile } from "antd"
import type { Dayjs } from "dayjs"

import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"

type NormalizeDate = (
  date: string | Date | Dayjs | null | undefined
) => string | null

export const isInvoiceAmountAbovePayment = (
  invoiceAmount: number | string | null | undefined,
  paymentAmount: number | null | undefined
): boolean =>
  paymentAmount != null &&
  paymentAmount > 0 &&
  invoiceAmount != null &&
  Number(invoiceAmount) > paymentAmount

export const linkedLinesTotal = (
  linkedLines: Record<string, number>
): number =>
  Object.values(linkedLines).reduce(
    (total, amount) => total + (Number(amount) || 0),
    0
  )

export const defaultLinkedLineAmount = (
  balanceRemaining: string | number | null | undefined,
  invoiceAmount: number | null | undefined,
  alreadyLinked: number
): number => {
  const balance = Math.max(Number(balanceRemaining) || 0, 0)
  if (invoiceAmount == null) return balance
  return Math.max(Math.min(balance, invoiceAmount - alreadyLinked), 0)
}

export const linkedLinesExceedInvoice = (
  linkedLines: Record<string, number>,
  invoiceAmount: number | null | undefined
): boolean =>
  invoiceAmount != null && linkedLinesTotal(linkedLines) > invoiceAmount

export const buildInvoiceCreatePayload = (
  values: ProjectInvoice,
  projectId: string,
  normalizeDate: NormalizeDate
): Partial<ProjectInvoice> => ({
  ...values,
  project: projectId,
  service_type: values.service_type || null,
  date_issued: normalizeDate(values.date_issued) ?? undefined,
  due_date: normalizeDate(values.due_date) ?? undefined
})

export interface UploadOutcome {
  doneUids: string[]
  failedNames: string[]
}

export const partitionUploadResults = (
  pending: UploadFile[],
  results: PromiseSettledResult<unknown>[]
): UploadOutcome => {
  const doneUids: string[] = []
  const failedNames: string[] = []

  pending.forEach((file, index) => {
    if (results[index]?.status === "fulfilled") {
      doneUids.push(file.uid)
    } else {
      failedNames.push(file.name)
    }
  })

  return { doneUids, failedNames }
}
